import React, { useState, useEffect, useCallback } from 'react';
import { WhatsAppConnection, ChatbotConfig, ChatbotTemplateInfo } from '../../types/config';
import { apiService } from '../../services/api';

interface ChatbotSettingsProps {
  connection: WhatsAppConnection;
  onSettingsChange: (settings: Partial<WhatsAppConnection>) => void;
}

interface ChatbotData {
  chatbot_enabled: boolean;
  chatbot_type: 'default' | 'custom';
  chatbot_messages: Record<string, string>;
  chatbot_config: ChatbotConfig;
  default_messages: Record<string, string>;
  default_config: ChatbotConfig;
  template_info: ChatbotTemplateInfo[];
}

const ChatbotSettings: React.FC<ChatbotSettingsProps> = ({ connection, onSettingsChange }) => {
  const [activeTab, setActiveTab] = useState<'general' | 'messages' | 'config'>('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chatbotData, setChatbotData] = useState<ChatbotData | null>(null);
  const [editingMessage, setEditingMessage] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Estado local
  const [enabled, setEnabled] = useState(connection.chatbot_enabled ?? true);
  const [chatbotType, setChatbotType] = useState<'default' | 'custom'>(connection.chatbot_type || 'default');
  const [customMessages, setCustomMessages] = useState<Record<string, string>>(connection.chatbot_messages || {});
  const [config, setConfig] = useState<ChatbotConfig>(connection.chatbot_config || {});

  const loadChatbotConfig = useCallback(async () => {
    if (!connection.id) return;
    setLoading(true);
    try {
      const response: any = await apiService.getCustom(`/whatsapp/${connection.id}/chatbot`);
      if (response.success) {
        const data = response.data as ChatbotData;
        setChatbotData(data);
        setEnabled(data.chatbot_enabled);
        setChatbotType(data.chatbot_type);
        setConfig(data.chatbot_config);

        // Calcular quais mensagens foram personalizadas (diferem do padrão)
        const customMsgs: Record<string, string> = {};
        if (data.chatbot_messages && data.default_messages) {
          for (const [key, value] of Object.entries(data.chatbot_messages)) {
            if (data.default_messages[key] !== value) {
              customMsgs[key] = value;
            }
          }
        }
        setCustomMessages(customMsgs);
      }
    } catch (error) {
      console.error('Erro ao carregar configuracao do chatbot:', error);
    } finally {
      setLoading(false);
    }
  }, [connection.id]);

  useEffect(() => {
    loadChatbotConfig();
  }, [loadChatbotConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData: Partial<WhatsAppConnection> = {
        chatbot_enabled: enabled,
        chatbot_type: chatbotType,
        chatbot_messages: customMessages,
        chatbot_config: config,
      };

      await apiService.putCustom(`/whatsapp/${connection.id}/chatbot`, updateData);
      onSettingsChange(updateData);
      setSuccessMessage('Configuracoes salvas com sucesso!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erro ao salvar configuracao do chatbot:', error);
      setErrorMessage('Erro ao salvar configuracoes do chatbot. Tente novamente.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleResetMessages = async () => {
    if (!window.confirm('Tem certeza que deseja resetar todas as mensagens para os padroes?')) return;

    setSaving(true);
    try {
      await apiService.postCustom(`/whatsapp/${connection.id}/chatbot/reset`, {});
      setCustomMessages({});
      await loadChatbotConfig();
      setSuccessMessage('Mensagens resetadas para os padroes!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Erro ao resetar mensagens:', error);
      setErrorMessage('Erro ao resetar mensagens. Tente novamente.');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleEditMessage = (key: string) => {
    const current = customMessages[key] || chatbotData?.default_messages[key] || '';
    setEditingMessage(key);
    setEditValue(current);
  };

  const handleSaveMessage = (key: string) => {
    const defaultValue = chatbotData?.default_messages[key] || '';
    const newMessages = { ...customMessages };

    if (editValue === defaultValue) {
      delete newMessages[key];
    } else {
      newMessages[key] = editValue;
    }

    setCustomMessages(newMessages);
    setEditingMessage(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditValue('');
  };

  const isMessageCustomized = (key: string) => {
    return key in customMessages;
  };

  // Agrupar templates por grupo
  const groupedTemplates = chatbotData?.template_info.reduce((acc, tmpl) => {
    if (!acc[tmpl.group]) acc[tmpl.group] = [];
    acc[tmpl.group].push(tmpl);
    return acc;
  }, {} as Record<string, ChatbotTemplateInfo[]>) || {};

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
        Carregando configuracoes do chatbot...
      </div>
    );
  }

  return (
    <div className="chatbot-settings">
      {successMessage && (
        <div className="alert alert-info" style={{ background: '#d4edda', borderColor: '#c3e6cb', color: '#155724', margin: '0 0 15px' }}>
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-warning" style={{ background: '#f8d7da', borderColor: '#f5c6cb', color: '#721c24', margin: '0 0 15px' }}>
          <i className="fas fa-exclamation-circle"></i>
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Tabs - usa mesmas classes CSS do WhatsAppAdvancedSettings */}
      <div className="settings-tabs">
        <button
          type="button"
          className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          <i className="fas fa-sliders-h"></i> Geral
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <i className="fas fa-comment-dots"></i> Mensagens
        </button>
        <button
          type="button"
          className={`tab-button ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <i className="fas fa-cogs"></i> Configurações
        </button>
      </div>

      <div className="settings-content">
      {/* Tab Geral */}
      {activeTab === 'general' && (
        <div>
          {/* Toggle Chatbot */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <div>
              <strong style={{ fontSize: '15px' }}>Chatbot Ativo</strong>
              <p style={{ margin: '5px 0 0', color: '#666', fontSize: '13px' }}>
                Ativa ou desativa o atendimento automatico via WhatsApp
              </p>
            </div>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute',
                cursor: 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: enabled ? '#28a745' : '#ccc',
                borderRadius: '26px',
                transition: '0.3s',
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '20px',
                  width: '20px',
                  left: enabled ? '27px' : '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: '0.3s',
                }} />
              </span>
            </label>
          </div>

          {/* Tipo de Chatbot */}
          <div style={{
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <strong style={{ fontSize: '15px', display: 'block', marginBottom: '10px' }}>
              Tipo de Chatbot
            </strong>
            <p style={{ margin: '0 0 12px', color: '#666', fontSize: '13px' }}>
              Escolha entre o chatbot padrao (checklist integrado) ou automacoes personalizadas
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              <label style={{
                flex: 1,
                padding: '12px',
                border: chatbotType === 'default' ? '2px solid #007bff' : '2px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                background: chatbotType === 'default' ? '#e7f1ff' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name="chatbot_type"
                  value="default"
                  checked={chatbotType === 'default'}
                  onChange={() => setChatbotType('default')}
                  style={{ marginRight: '8px' }}
                />
                <strong>Chatbot Padrao</strong>
                <p style={{ margin: '5px 0 0 22px', color: '#666', fontSize: '12px' }}>
                  Checklist integrado com fluxo de inspecao de veiculos. Pronto para usar.
                </p>
              </label>

              <label style={{
                flex: 1,
                padding: '12px',
                border: chatbotType === 'custom' ? '2px solid #007bff' : '2px solid #dee2e6',
                borderRadius: '8px',
                cursor: 'pointer',
                background: chatbotType === 'custom' ? '#e7f1ff' : 'white',
                transition: 'all 0.2s'
              }}>
                <input
                  type="radio"
                  name="chatbot_type"
                  value="custom"
                  checked={chatbotType === 'custom'}
                  onChange={() => setChatbotType('custom')}
                  style={{ marginRight: '8px' }}
                />
                <strong>Automacoes</strong>
                <p style={{ margin: '5px 0 0 22px', color: '#666', fontSize: '12px' }}>
                  Usar fluxos de automacao personalizados criados em /automacoes.
                </p>
              </label>
            </div>
          </div>

          {chatbotType === 'default' && (
            <div style={{
              padding: '12px 15px',
              background: '#fff3cd',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#856404',
              border: '1px solid #ffc107'
            }}>
              O chatbot padrao guia o motorista pelo fluxo: Menu &rarr; Selecionar Veiculo &rarr;
              Tipo de Checklist &rarr; Responder Itens &rarr; Finalizar. As mensagens podem ser
              personalizadas na aba "Mensagens".
            </div>
          )}

          {chatbotType === 'custom' && (
            <div style={{
              padding: '12px 15px',
              background: '#d1ecf1',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#0c5460',
              border: '1px solid #bee5eb'
            }}>
              As mensagens recebidas serao processadas pelos fluxos de automacao configurados
              em <strong>/automacoes</strong>. O chatbot padrao sera desativado.
            </div>
          )}
        </div>
      )}

      {/* Tab Mensagens */}
      {activeTab === 'messages' && chatbotData && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
              Personalize as mensagens do chatbot. Mensagens nao alteradas usam o padrao.
            </p>
            <button
              type="button"
              onClick={handleResetMessages}
              disabled={saving || Object.keys(customMessages).length === 0}
              style={{
                padding: '6px 12px',
                border: '1px solid #dc3545',
                borderRadius: '6px',
                background: 'white',
                color: '#dc3545',
                cursor: Object.keys(customMessages).length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                opacity: Object.keys(customMessages).length === 0 ? 0.5 : 1,
              }}
            >
              Resetar Tudo
            </button>
          </div>

          {Object.entries(groupedTemplates).map(([group, templates]) => (
            <div key={group} style={{ marginBottom: '20px' }}>
              <h4 style={{
                fontSize: '14px',
                color: '#333',
                borderBottom: '1px solid #e9ecef',
                paddingBottom: '8px',
                marginBottom: '10px'
              }}>
                {group}
              </h4>

              {templates.map(tmpl => (
                <div
                  key={tmpl.key}
                  style={{
                    padding: '10px 12px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    background: isMessageCustomized(tmpl.key) ? '#fff8e1' : 'white'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '13px' }}>{tmpl.label}</strong>
                      <span style={{ fontSize: '11px', color: '#999', marginLeft: '8px' }}>
                        {tmpl.description}
                      </span>
                      {isMessageCustomized(tmpl.key) && (
                        <span style={{
                          fontSize: '10px',
                          color: '#e67e22',
                          marginLeft: '8px',
                          fontWeight: 'bold'
                        }}>
                          (personalizada)
                        </span>
                      )}
                      {tmpl.variables.length > 0 && (
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>
                          Variaveis: {tmpl.variables.map(v => `{${v}}`).join(', ')}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleEditMessage(tmpl.key)}
                      style={{
                        padding: '4px 10px',
                        border: '1px solid #007bff',
                        borderRadius: '4px',
                        background: 'white',
                        color: '#007bff',
                        cursor: 'pointer',
                        fontSize: '12px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Editar
                    </button>
                  </div>

                  {editingMessage === tmpl.key && (
                    <div style={{ marginTop: '10px' }}>
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        rows={5}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          fontSize: '13px',
                          fontFamily: 'monospace',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                      />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #6c757d',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#6c757d',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            // Restaurar padrao
                            const defaultMsg = chatbotData?.default_messages[tmpl.key] || '';
                            setEditValue(defaultMsg);
                          }}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #ffc107',
                            borderRadius: '4px',
                            background: '#fff3cd',
                            color: '#856404',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Restaurar Padrao
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveMessage(tmpl.key)}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            background: '#28a745',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tab Configuracoes */}
      {activeTab === 'config' && (
        <div>
          <div style={{
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <label style={{ display: 'block', marginBottom: '12px' }}>
              <strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                Timeout de Sessao (minutos)
              </strong>
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px' }}>
                Tempo de inatividade antes de encerrar a sessao do motorista
              </span>
              <input
                type="number"
                value={config.sessionTimeoutMinutes ?? 30}
                onChange={(e) => setConfig({ ...config, sessionTimeoutMinutes: parseInt(e.target.value) || 30 })}
                min={5}
                max={120}
                style={{
                  width: '100px',
                  padding: '6px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: '12px' }}>
              <strong style={{ fontSize: '13px', display: 'block', marginBottom: '4px' }}>
                Max. Tentativas Invalidas
              </strong>
              <span style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px' }}>
                Quantidade de respostas invalidas antes de encerrar a sessao
              </span>
              <input
                type="number"
                value={config.maxInvalidAttempts ?? 5}
                onChange={(e) => setConfig({ ...config, maxInvalidAttempts: parseInt(e.target.value) || 5 })}
                min={3}
                max={20}
                style={{
                  width: '100px',
                  padding: '6px 8px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </label>
          </div>

          <div style={{
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '15px'
          }}>
            <strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
              Comportamento em Nao Conformidades
            </strong>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input
                type="checkbox"
                checked={config.askPhotoOnProblem ?? true}
                onChange={(e) => setConfig({ ...config, askPhotoOnProblem: e.target.checked })}
              />
              Pedir foto ao reportar nao conformidade
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input
                type="checkbox"
                checked={config.notifyManagerOnNonConformity ?? true}
                onChange={(e) => setConfig({ ...config, notifyManagerOnNonConformity: e.target.checked })}
              />
              Notificar gestor sobre nao conformidades
            </label>

            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '10px',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input
                type="checkbox"
                checked={config.askObservationOnNonConformity ?? true}
                onChange={(e) => setConfig({ ...config, askObservationOnNonConformity: e.target.checked })}
              />
              Pedir descricao/observacao ao reportar nao conformidade
            </label>
          </div>
        </div>
      )}

      {/* Botao Salvar */}
      <div className="settings-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} style={{ marginRight: 6 }}></i>
          {saving ? 'Salvando...' : 'Salvar Configurações do Chatbot'}
        </button>
      </div>
      </div>
    </div>
  );
};

export default ChatbotSettings;
